import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Package, TrendingUp, DollarSign, Warehouse, ChevronDown, ChevronUp, AlertCircle, CreditCard } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Consignment() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [soldData, setSoldData] = useState(null);
  const [expandedSuppliers, setExpandedSuppliers] = useState({});
  const [selectedTab, setSelectedTab] = useState("on-hand");
  
  // Date filters for sold report
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  // Supplier detail modal
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showSupplierDetail, setShowSupplierDetail] = useState(false);

  useEffect(() => {
    fetchSummary();
  }, []);

  useEffect(() => {
    if (selectedTab === "sold") {
      fetchSoldData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, startDate, endDate]);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/consignment/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error("Error fetching consignment summary:", error);
      toast.error("Failed to load consignment summary");
    } finally {
      setLoading(false);
    }
  };

  const fetchSoldData = async () => {
    try {
      const response = await axios.get(`${API}/consignment/sold`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setSoldData(response.data);
    } catch (error) {
      console.error("Error fetching consignment sold data:", error);
      toast.error("Failed to load sold data");
    }
  };

  const toggleSupplierExpand = (supplierId) => {
    setExpandedSuppliers(prev => ({
      ...prev,
      [supplierId]: !prev[supplierId]
    }));
  };

  const handleSupplierClick = (supplier) => {
    setSelectedSupplier(supplier);
    setShowSupplierDetail(true);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-gray-500">Loading consignment data...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="consignment-page">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Consignment Stock</h1>
          <p className="text-gray-600">Track stock owned by suppliers until sold</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Package className="h-8 w-8 text-amber-600" />
              <div>
                <p className="text-sm text-amber-700">Consignment Products</p>
                <p className="text-2xl font-bold text-amber-900">{summary?.total_products || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Warehouse className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-sm text-blue-700">Items On Hand</p>
                <p className="text-2xl font-bold text-blue-900">{(summary?.total_items_on_hand || 0).toFixed(0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-sm text-green-700">Stock Value (At Cost)</p>
                <p className="text-2xl font-bold text-green-900">R {(summary?.total_stock_value || 0).toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-purple-600" />
              <div>
                <p className="text-sm text-purple-700">Suppliers With Consignment</p>
                <p className="text-2xl font-bold text-purple-900">{summary?.by_supplier?.length || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="on-hand">Stock On Hand</TabsTrigger>
          <TabsTrigger value="sold">Sold (Owe Suppliers)</TabsTrigger>
        </TabsList>

        {/* On Hand Tab */}
        <TabsContent value="on-hand" className="mt-4">
          {summary?.by_supplier?.length === 0 ? (
            <Card className="p-8 text-center">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">No consignment products found</p>
              <p className="text-sm text-gray-500">
                Mark products as &quot;Consignment Stock&quot; in the Product Master to track them here.
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              {summary?.by_supplier?.map((supplier) => (
                <Card key={supplier.supplier_id || 'no-supplier'} className="overflow-hidden">
                  <CardHeader 
                    className="bg-amber-50 cursor-pointer hover:bg-amber-100 transition-colors"
                    onClick={() => toggleSupplierExpand(supplier.supplier_id)}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        {expandedSuppliers[supplier.supplier_id] ? (
                          <ChevronUp className="h-5 w-5 text-amber-600" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-amber-600" />
                        )}
                        <CardTitle className="text-lg">{supplier.supplier_name}</CardTitle>
                        <span className="text-sm text-gray-500">({supplier.product_count} products)</span>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Items On Hand</p>
                          <p className="font-semibold">{supplier.total_items.toFixed(0)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Stock Value</p>
                          <p className="font-bold text-amber-700">R {supplier.total_value.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  
                  {expandedSuppliers[supplier.supplier_id] && (
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead>PLU</TableHead>
                            <TableHead>Product Name</TableHead>
                            <TableHead className="text-right">Stock</TableHead>
                            <TableHead className="text-right">Unit Cost</TableHead>
                            <TableHead className="text-right">Stock Value</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {supplier.products.map((product) => (
                            <TableRow key={product.id}>
                              <TableCell className="font-mono text-sm">{product.plu_code}</TableCell>
                              <TableCell>{product.name}</TableCell>
                              <TableCell className="text-right">{product.current_stock}</TableCell>
                              <TableCell className="text-right">R {product.unit_cost.toFixed(2)}</TableCell>
                              <TableCell className="text-right font-medium">R {product.stock_value.toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Sold Tab */}
        <TabsContent value="sold" className="mt-4 space-y-4">
          {/* Date Filters */}
          <Card className="p-4">
            <div className="flex items-end gap-4">
              <div>
                <Label>From Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-40"
                />
              </div>
              <div>
                <Label>To Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-40"
                />
              </div>
              <Button onClick={fetchSoldData} variant="outline">
                Update Report
              </Button>
            </div>
          </Card>

          {/* Sold Summary */}
          {soldData && (
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <CreditCard className="h-8 w-8 text-red-600" />
                    <div>
                      <p className="text-sm text-red-700">Total Owed to Suppliers</p>
                      <p className="text-2xl font-bold text-red-900">R {(soldData.total_sold_value || 0).toFixed(2)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-50 border-gray-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Package className="h-8 w-8 text-gray-600" />
                    <div>
                      <p className="text-sm text-gray-700">Items Sold (Consignment)</p>
                      <p className="text-2xl font-bold text-gray-900">{(soldData.total_items_sold || 0).toFixed(0)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Sold by Supplier */}
          {soldData?.by_supplier?.length === 0 ? (
            <Card className="p-8 text-center">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">No consignment items sold in this period</p>
              <p className="text-sm text-gray-500">
                Consignment sales will appear here when products marked as consignment are sold.
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              {soldData?.by_supplier?.map((supplier) => (
                <Card key={supplier.supplier_id || 'no-supplier'} className="overflow-hidden">
                  <CardHeader 
                    className="bg-red-50 cursor-pointer hover:bg-red-100 transition-colors"
                    onClick={() => toggleSupplierExpand(`sold-${supplier.supplier_id}`)}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        {expandedSuppliers[`sold-${supplier.supplier_id}`] ? (
                          <ChevronUp className="h-5 w-5 text-red-600" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-red-600" />
                        )}
                        <CardTitle className="text-lg">{supplier.supplier_name}</CardTitle>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Items Sold</p>
                          <p className="font-semibold">{supplier.items_sold.toFixed(0)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Amount Owed</p>
                          <p className="font-bold text-red-700">R {supplier.total_owed.toFixed(2)}</p>
                        </div>
                        <Button 
                          size="sm" 
                          className="bg-red-600 hover:bg-red-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSupplierClick(supplier);
                          }}
                        >
                          <CreditCard className="h-4 w-4 mr-1" />
                          Pay Supplier
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  
                  {expandedSuppliers[`sold-${supplier.supplier_id}`] && (
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead>PLU</TableHead>
                            <TableHead>Product Name</TableHead>
                            <TableHead className="text-right">Qty Sold</TableHead>
                            <TableHead className="text-right">Unit Cost</TableHead>
                            <TableHead className="text-right">Total Owed</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {supplier.products.map((product, idx) => (
                            <TableRow key={`${product.product_id}-${idx}`}>
                              <TableCell className="font-mono text-sm">{product.plu_code}</TableCell>
                              <TableCell>{product.product_name}</TableCell>
                              <TableCell className="text-right">{product.quantity_sold}</TableCell>
                              <TableCell className="text-right">R {product.unit_cost.toFixed(2)}</TableCell>
                              <TableCell className="text-right font-medium text-red-700">R {product.total_cost.toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Supplier Payment Detail Modal */}
      <Dialog open={showSupplierDetail} onOpenChange={setShowSupplierDetail}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-red-600" />
              Suggested Payment - {selectedSupplier?.supplier_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedSupplier && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-700">Amount Owed for Consignment Sales</p>
                <p className="text-3xl font-bold text-red-900">R {selectedSupplier.total_owed.toFixed(2)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Period: {startDate} to {endDate}
                </p>
              </div>

              <div>
                <Label className="text-sm font-medium">Products Sold:</Label>
                <div className="mt-2 max-h-48 overflow-y-auto border rounded-lg divide-y">
                  {selectedSupplier.products.map((product, idx) => (
                    <div key={idx} className="p-2 flex justify-between text-sm">
                      <span>{product.product_name}</span>
                      <span className="font-medium">
                        {product.quantity_sold} × R{product.unit_cost.toFixed(2)} = R{product.total_cost.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={() => {
                    // Navigate to supplier page to make payment
                    window.location.href = `/suppliers?id=${selectedSupplier.supplier_id}&pay=${selectedSupplier.total_owed.toFixed(2)}`;
                  }}
                >
                  Go to Supplier to Pay
                </Button>
                <Button variant="outline" onClick={() => setShowSupplierDetail(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
